import React from 'react';
import { DatePicker, Paragraph, Title } from '@cambridgeassessment/cambridge-ui';
import { DATE_DISPLAY_FORMAT } from '../../../../constants';
import { StyledInputContainer } from '../Series.styles';
import { convertToMidnight, Series, Roles } from '@cambridgeassessment/lib-ee-commons';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import thunks from '../../../../store/series/thunks';
import { RootState } from '../../../../store';
import { Authorized } from 'lib-browser';

interface DeletionDateProps {
  series: Series | null;
  deletionDate: Series['deletionDate'];
}

const qaKeyBuilder = (seriesId: number) => (key: string) => {
  return `series-${seriesId}-deletion-date-${key}`;
};

const DeletionDate: React.FC<DeletionDateProps> = ({ series, deletionDate }) => {
  const dispatch = useDispatch();
  const selectedRole = useSelector((state: RootState) => state.userData.selectedRole);
  if (!series || !deletionDate) {
    return null;
  }
  const toQAKey = qaKeyBuilder(series.id);

  const handleUpdate = (date: moment.Moment) => {
    const newDate = date.parseZone();
    dispatch(thunks.updateSeriesDateByFieldName(series._id, newDate, 'deletionDate'));
  };

  const value = moment.parseZone(deletionDate).toISOString();

  const RenderComponent = (props?: { readOnly?: boolean }) => (
    <StyledInputContainer>
      <DatePicker
        dataQaId={toQAKey('datepicker')}
        key={deletionDate}
        onChange={handleUpdate}
        value={convertToMidnight(value)}
        format={DATE_DISPLAY_FORMAT}
        disabled={props?.readOnly}
      />
    </StyledInputContainer>
  );

  return (
    <>
      <Title data-qa-id={toQAKey('title')}>Deletion date</Title>
      <Paragraph data-qa-id={toQAKey('p1')}>
        Change the date in which files submitted for the given series will be deleted from Amazon Web Services.
      </Paragraph>
      <Paragraph data-qa-id={toQAKey('p2')}>
        This should be the date they are no longer operationally or legally required. Note files are copied to the Data
        Lake at point of submission also.
      </Paragraph>
      <Authorized
        selectedRole={selectedRole}
        roles={[Roles.SUBMIT_SUPER_ADMIN]}
        readOnlyRoles={[Roles.SUBMIT_COMPLIANCE, Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER, Roles.SUBMIT_ADMINISTRATOR]}
      >
        <RenderComponent />
      </Authorized>
    </>
  );
};

export default DeletionDate;
